import { jsx, jsxs } from 'react/jsx-runtime';
import { useState } from 'react';
import { twMerge } from '@belong/common/twMerge';
import { useIsMobileDevice } from '@belong/common/useIsMobileDevice';
import { IconInfo } from '@belong/icons/dist/IconInfo';
import * as PopoverPrimitive from '@radix-ui/react-popover';
import * as TooltipPrimitive from '@radix-ui/react-tooltip';
import { ConditionalWrap } from '../ConditionalWrap/ConditionalWrap.js';

const CONTENT_MOTION = 'origin-[var(--radix-tooltip-content-transform-origin)] animate-tooltipIn data-[state=closed]:animate-tooltipOut motion-reduce:animate-none';
/**
 * If you have this tooltip inside another actionable element
 * you may need to pass the children manually and prevent the event propagation, ie:
 * <button onClick={(e) => e.stopPropagation()} type="button" >
 *   <IconInfo width={20} />
 * </button>
 */
/**
 * Note for the mobile approach:
 * Since the Tooltip component is not responsive by design https://github.com/radix-ui/primitives/issues/955#issuecomment-960610209
 * we are using the Popover component for mobile devices and the Tooltip component for desktop devices.
 */
function Tooltip({
  children = jsx("button", {
    className: "cursor-pointer fill-navy",
    type: "button",
    children: jsx(IconInfo, {
      width: 20
    })
  }),
  content,
  open: controlledOpen,
  defaultOpen,
  onOpenChange,
  contentProps,
  arrowProps = {
    className: 'fill-navy'
  },
  portal = false
}) {
  const isMobile = useIsMobileDevice();
  const [isOpen, setIsOpen] = useState(defaultOpen || false);
  const handleOpenChange = newOpenState => {
    setIsOpen(newOpenState);
    onOpenChange?.(newOpenState);
  };
  const handleTriggerClick = () => {
    handleOpenChange(!isOpen);
  };
  if (isMobile) {
    return jsxs(PopoverPrimitive.Root, {
      open: controlledOpen ?? isOpen,
      onOpenChange: handleOpenChange,
      children: [jsx(PopoverPrimitive.Trigger, {
        asChild: true,
        onClick: handleTriggerClick,
        children: children
      }), jsx(ConditionalWrap, {
        condition: portal,
        wrapper: children => jsx(PopoverPrimitive.Portal, {
          children: children
        }),
        children: jsxs(PopoverPrimitive.Content, {
          ...contentProps,
          className: twMerge(CONTENT_MOTION, contentProps?.className),
          children: [content, jsx(PopoverPrimitive.Arrow, {
            ...arrowProps
          })]
        })
      })]
    });
  }
  return jsxs(TooltipPrimitive.Root, {
    open: controlledOpen,
    defaultOpen: defaultOpen,
    onOpenChange: onOpenChange,
    children: [jsx(TooltipPrimitive.Trigger, {
      asChild: true,
      children: children
    }), jsx(ConditionalWrap, {
      condition: portal,
      wrapper: children => jsx(TooltipPrimitive.Portal, {
        children: children
      }),
      children: jsxs(TooltipPrimitive.Content, {
        ...contentProps,
        className: twMerge(CONTENT_MOTION, contentProps?.className),
        children: [content, jsx(TooltipPrimitive.Arrow, {
          ...arrowProps
        })]
      })
    })]
  });
}

export { Tooltip };
